import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import PrepareClaimFilter from "./PrepareClaimFilter";
import PrepareClaimTable from "./PrepareClaimTable";
import BackButton from "components/common/custom/back-button";
import paths from "routes/paths";
import {
  ClaimBatchCheck,
  CreateClaimFromPreauth,
  GetPrepareClaimService,
} from "../config/cliamApi";
import {
  handleResetPrepareClaim,
  addValidationError,
  clearValidationErrors,
  handleResetCreateClaim,
  handleResetClaimAll,
  handleResetServiceSplit,
  setSelectedService,
  handleUpdateSplit,
  handlePreauthFormData,
} from "../config/claimSlice";
import moment from "moment";
import { toast } from "react-toastify";
import ClaimBatchCreateCLiamFromPreauthModal from "./ClaimBatchCreateCLiamFromPreauthModal";
import { useNavigate } from "react-router-dom";
import { getToday } from "helpers/utils";
import { handleResetAll } from "features/preauthorization/config/preauthorizationSlice";
import { handleResetPatientSearch } from "features/eliglibility/eliglibility-check/config/eligibilityCheckSlice";

const PrepareClaim = () => {
  const dispatch = useDispatch();
  const { prepareClaimFilters, prepareClaimsServices, serviceSplits } =
    useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );

  const filteredData = prepareClaimsServices?.filter((row) => {
    const items = Array.isArray(row.item)
      ? row.item
      : Object.values(row.item || {});
    const hasValidServiceCode = items.some(
      (item) => item.prd_code && item.prd_code.trim() !== ""
    );
    return hasValidServiceCode;
  });

  useEffect(() => {
    dispatch(handleResetAll({ check: "reset" }));
    dispatch(handleResetCreateClaim());
    dispatch(handlePreauthFormData({ check: "reset" }));
    dispatch(handleResetPatientSearch());
  }, []);

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { isSubmitted, errors },
  } = useForm({
    defaultValues: {
      branch: "",
      payer: "",
      insurance: "",
      start_date: "",
      end_date: "",
    },
  });

  const selectBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const navigate = useNavigate();
  const [totalRows, setTotalRows] = useState(0);
  const [claimNo, setClaimNo] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [body, setBody] = useState(null);
  const [data, setData] = useState([]);

  const requestBody = (customPage = page, customLimit = rowsPerPage) => ({
    branch_id:
      prepareClaimFilters?.branch?.branch_id ||
      selectBranch?.branch_id ||
      selectBranch?.value ||
      "",
    insurance_company_id: prepareClaimFilters?.insurance?.insurance_company_id,
    claim_type: prepareClaimFilters?.claim_type?.claim_types_id,
    start_date: prepareClaimFilters?.start_date
      ? moment(prepareClaimFilters?.start_date).format("YYYY-MM-DD")
      : "",
    end_date: prepareClaimFilters?.end_date
      ? moment(prepareClaimFilters?.end_date).format("YYYY-MM-DD")
      : "",
    start_point: customPage,
    limit: customLimit,
  });

  const fetchPrepareClaimList = async (body = {}) => {
    dispatch(handleResetServiceSplit());

    const response = await dispatch(GetPrepareClaimService(body)).unwrap();
    setTotalRows(response?.total_rows || 0);
    if (!response?.success) {
      toast.error("Something Went Wrong");
    }
  };

  useEffect(() => {
    const body = requestBody(page, rowsPerPage);
    if (prepareClaimFilters?.insurance && prepareClaimFilters?.start_date) {
      fetchPrepareClaimList(body);
    }
  }, [page, rowsPerPage]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(0);
  }, []);

  const handleCheckListFilter = () => {
    const newPage = 0;
    setPage(newPage);
    const body = requestBody(newPage, rowsPerPage);
    fetchPrepareClaimList(body);
  };

  const handleReset = async () => {
    const body = {
      branch_id: "",
      insurance_company_id: "",
      claim_type: "",
      start_date: "",
      end_date: "",
      start_point: 0,
      limit: 10,
    };
    setPage(0);
    setRowsPerPage(10);
    reset();
    dispatch(handleResetPrepareClaim());
    await fetchPrepareClaimList(body);
  };

  console.log({ prepareClaimsServices });

  const handleSubmitClaim = async () => {
    const claimTypeMap = {
      institutional: "1",
      vision: "5",
      pharmacy: "3",
      oral: "2",
      professional: "4",
    };

    const validationErrors = {};
    let hasErrors = false;
    const emptyPeriodSplits = [];

    // Validate invoice_no and collect splits with empty period
    Object.entries(serviceSplits).forEach(([preauthId, services]) => {
      services.forEach((service, serviceIndex) => {
        service.splits.forEach((split, splitIndex) => {
          // Validate invoice_no
          if (!split.invoice_no || split.invoice_no.trim() === "") {
            hasErrors = true;
            validationErrors[split.id] = {
              ...(validationErrors[split.id] || {}),
              invoice_no: "Invoice number is required",
            };
          }
          // Collect splits with empty or invalid period
          if (!split.period?.start || !split.period?.end) {
            emptyPeriodSplits.push({
              preauthId,
              serviceIndex,
              splitIndex,
              split,
            });
          }
        });
      });
    });

    if (hasErrors) {
      dispatch(addValidationError(validationErrors));
      toast.error("Please fill in all invoice numbers before submitting.");
      return;
    }

    // Handle empty periods by copying from nearest split
    emptyPeriodSplits.forEach(({ preauthId, serviceIndex, splitIndex }) => {
      const service = serviceSplits[preauthId][serviceIndex];
      const splits = service.splits;

      // Find the nearest split with a valid period
      let nearestPeriod = null;
      let minDistance = Infinity;

      splits.forEach((otherSplit, otherIndex) => {
        if (
          otherIndex !== splitIndex &&
          otherSplit.period?.start &&
          otherSplit.period?.end
        ) {
          const distance = Math.abs(splitIndex - otherIndex);
          if (distance < minDistance) {
            minDistance = distance;
            nearestPeriod = { ...otherSplit.period };
          }
        }
      });

      console.log({ nearestPeriod });
      // Update the split with the nearest period or default to today
      const updatedPeriod = nearestPeriod || {
        start: service?.period,
        end: service?.period,
      };
      console.log({ updatedPeriod });

      dispatch(
        handleUpdateSplit({
          preauthId,
          serviceIndex,
          split: {
            ...splits[splitIndex],
            period: updatedPeriod,
          },
        })
      );
    });

    dispatch(clearValidationErrors());

    const claimData = prepareClaimsServices
      ?.filter((row) => {
        const items = Array.isArray(row.item)
          ? row.item
          : Object.values(row.item || {});
        if (items.length > 0) {
          return items.some(
            (item) => item.prd_code && item.prd_code.trim() !== ""
          );
        }
        return true;
      })
      .reduce((acc, preauth, index) => {
        if (!preauth.diag || !preauth.item) return acc;

        const splits = serviceSplits[preauth.preauthfull_id] || [];

        // Compute min start date (admission) and max end date (discharge) based on all service splits
        let minStartDate = null;
        let maxEndDate = null;

        splits.forEach((service) => {
          service.splits.forEach((split) => {
            if (split.period?.start) {
              const startDate = moment(split.period.start);
              if (!minStartDate || startDate.isBefore(minStartDate)) {
                minStartDate = startDate;
              }
            }
            if (split.period?.end) {
              const endDate = moment(split.period.end);
              if (!maxEndDate || endDate.isAfter(maxEndDate)) {
                maxEndDate = endDate;
              }
            }
          });
        });

        // Set admission date to min start date
        const enct_stdate = minStartDate
          ? minStartDate.format("YYYY-MM-DD HH:mm:ss")
          : preauth?.enct_stdate
            ? moment(preauth?.enct_stdate).format("YYYY-MM-DD HH:mm:ss")
            : "";

        // Set discharge date: if original is null/empty, send null; else set to max end date

        const enct_eddate =
          maxEndDate && preauth?.enct_eddate
            ? maxEndDate.format("YYYY-MM-DD HH:mm:ss")
            : "";

        console.log({ testing: preauth?.enct_eddate });

        let flatIndex = 1;

        const prdSer = splits.reduce((services, service, serviceIndex) => {
          const item = Array.isArray(preauth.item)
            ? preauth.item[serviceIndex] || {}
            : preauth.item[serviceIndex + 1] || {};

          const splitsData = service.splits.map((split) => {
            const period =
              split.period?.start && split.period?.end
                ? { ...split.period }
                : { start: service?.period?.start, end: service?.period?.end }; // Fallback in case update missed
            // console.log({ period });
            const entry = {
              invoice_no: split.invoice_no || "10",
              prd_type: item.prd_type || service.prd_type || "",
              prd_code: item.prd_code || service.prd_code,
              prd_descp_txt: item.prd_descp_txt || service.prd_descp_txt,
              prov_cde: item.prov_cde || service.prov_cde || null,
              preauthref: preauth.preauthref || "",
              prd_url_txt: item.prd_url_txt || service.prd_url_txt || "",
              prd_pri: item.prd_pri || service.prd_pri || "0",
              prd_qty: split.quantity.toString(),
              prd_namnt: (
                Number(split.quantity) *
                Number(item.prd_pri || service.prd_pri || 0) *
                (1 - Number(item.discnt || service.discnt || 0) / 100)
              ).toFixed(2),
              discnt: item.discnt || service.discnt || "0",
              prd_pshre: split.patient_share || item.prd_pshre || "0",
              diag_tax: item.diag_tax || service.diag_tax || "0",
              period,
              prd_packg: item.prd_packg || service.prd_packg || false,
              prd_mtrnty: item.prd_mtrnty || service.prd_mtrnty || false,
              pract: item.pract || service.pract || ["1"],
              supinfo: item.supinfo ||
                service.supinfo || [
                  "addinfo",
                  "chfcmp",
                  "vreas",
                  "invres",
                  "trtpln",
                  "pathistry",
                  "phyexmn",
                  "histrypreill",
                ],
              pres_med: item.pres_med || service.pres_med || null,
              diag: item.diag || service.diag || ["I10"],
              eligible: item.eligible || service.eligible || "0",
              copay: split.patient_share || item.copay || "0",
              benefit: item.benefit || service.benefit || "0",
              "approved-quantity": split.quantity.toString(),
              // Pharmacy-specific fields from prepareClaimsServices
              ...(item.strngth && { strngth: item.strngth }),
              ...(item.pharsubs && { pharsubs: item.pharsubs }),
              ...(item.pselrsn && { pselrsn: item.pselrsn }),
              ...(item.slct_rsn && { slct_rsn: item.slct_rsn }),
              ...(item.Sntfc_code_abs_rsn && {
                Sntfc_code_abs_rsn: item.Sntfc_code_abs_rsn,
              }),
              ...(item.supinfo3 && { supinfo3: item.supinfo3 }),
            };
            return { key: flatIndex++, value: entry };
          });

          const obj = {};
          splitsData.forEach(({ key, value }) => {
            obj[key] = value;
          });

          return { ...services, ...obj };
        }, {});

        // Transform diag to ensure diag_onset defaults to "NR"
        const transformedDiag = Object.entries(preauth.diag || {}).reduce(
          (diagAcc, [key, diag]) => {
            diagAcc[key] = {
              ...diag,
              diag_onset: diag.diag_onset || "NR",
            };
            return diagAcc;
          },
          {}
        );

        acc[index + 1] = {
          claim_type: claimTypeMap[preauth.preauthclmtyp.toLowerCase()],
          process_prior: "normal",
          patient_preauthid: preauth.preauthfull_id,
          episodeid: "",
          enct_stdate,
          enct_eddate,
          diag:
            Object.keys(transformedDiag).length > 0
              ? transformedDiag
              : preauth.diag,

          prd_ser: Object.keys(prdSer).length > 0 ? prdSer : preauth.item,
        };

        return acc;
      }, {});

    const body = {
      branch_id:
        prepareClaimFilters?.branch?.branch_id ||
        selectBranch?.branch_id ||
        selectBranch?.value ||
        "",
      btch_month: prepareClaimFilters?.start_date
        ? moment(prepareClaimFilters?.start_date).format("MM-YYYY")
        : "",
      insurance_company_id:
        prepareClaimFilters?.insurance?.insurance_company_id || 11,
      ...(claimNo?.trim() && { claimbatch_no: claimNo.trim() }),
      claim_data: claimData,
    };

    const batchBody = {
      branch_id:
        prepareClaimFilters?.branch?.branch_id ||
        selectBranch?.branch_id ||
        selectBranch?.value ||
        "",
      insurance_company_id:
        prepareClaimFilters?.insurance?.insurance_company_id || "",
      claimbatch_month: prepareClaimFilters?.start_date
        ? moment(prepareClaimFilters?.start_date).format("MM-YYYY")
        : "",
    };

    const response = await dispatch(ClaimBatchCheck(batchBody)).unwrap();
    if (response?.success && response?.data?.batch_details?.length > 0) {
      setShowClaimModal(true);
      setData(response?.data?.batch_details);
      setBody(body);
    } else {
      const createResponse = await dispatch(
        CreateClaimFromPreauth(body)
      ).unwrap();
      if (createResponse?.success) {
        toast.success(createResponse?.message_en);
        navigate(paths.claimsList);
        dispatch(handleResetCreateClaim());
        dispatch(handleResetClaimAll());
      } else {
        toast.error("Failed to create claim");
      }
    }
  };

  return (
    <>
      {Number(claimsSetting?.add) === 1 ? (
        <Form
          noValidate
          onSubmit={handleSubmit(handleCheckListFilter)}
          className="p-0"
        >
          <FalconComponentCard
            className="shadow-md"
            style={{ position: "relative" }}
          >
            <FalconComponentCard.Header
              className="d-flex fixed-header justify-content-between align-items-center"
              sx={{
                position: "sticky !important",
                top: 68,
                zIndex: 1020,
                backgroundColor: "white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <BackButton path={paths.claimsList} title="Prepare Claims" />

              <Col xs="auto" className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={handleReset}
                >
                  Reset
                </Button>
                <Button type="submit" size="sm" variant="success">
                  Filter
                </Button>
                {Number(claimsSetting?.add) === 1 && (
                  <Button
                    size="sm"
                    variant="info"
                    disabled={filteredData?.length === 0}
                    onClick={handleSubmitClaim}
                  >
                    Create Claims
                  </Button>
                )}
              </Col>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body
              style={{
                marginTop: "40px",
                overflowY: "auto",
                maxHeight: "calc(100vh - 200px)",
              }}
            >
              <PrepareClaimFilter
                watch={watch}
                setValue={setValue}
                control={control}
                isSubmitted={isSubmitted}
                errors={errors}
              />
              <PrepareClaimTable
                data={prepareClaimsServices}
                totalRows={totalRows}
                handlePageChange={handlePageChange}
                handlePerRowsChange={handlePerRowsChange}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Form>
      ) : (
        <div className="py-6 text-center border rounded-3mb-3">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to prepare claims.
          </p>
        </div>
      )}
      <ClaimBatchCreateCLiamFromPreauthModal
        body={body}
        setBody={setBody}
        data={data}
        modal={showClaimModal}
        setModal={setShowClaimModal}
        setClaimNo={setClaimNo}
      />
    </>
  );
};

export default PrepareClaim;
